import { useCallback, useEffect, useState } from 'react'
import { useCurrentUserProfile } from 'hooks/queries/users/useCurrentUserProfile'

const STORAGE_KEY_PREFIX = 'whats_new_banner'
const DISMISSED_KEY_SUFFIX = 'dismissed'
const VISITED_KEY_SUFFIX = 'visited'

function getIsDismissed(userId: number | undefined): boolean {
  if (typeof window === 'undefined' || !userId) return false
  const storageKey = `${STORAGE_KEY_PREFIX}_${DISMISSED_KEY_SUFFIX}_${userId}`
  return localStorage.getItem(storageKey) !== null
}

function markVisitedAndGetHasVisitedBefore(userId: number | undefined): boolean {
  if (typeof window === 'undefined' || !userId) return false
  const visitKey = `${STORAGE_KEY_PREFIX}_${VISITED_KEY_SUFFIX}_${userId}`
  const hasVisitedBefore = localStorage.getItem(visitKey) !== null
  if (!hasVisitedBefore) {
    localStorage.setItem(visitKey, '1')
  }
  return hasVisitedBefore
}

/**
 * Hook to manage visibility of the What's New banner.
 * Don't show on first visit (let users orient to the layout); show from second visit onward.
 * When dismissed, never show again.
 */
export function useWhatsNewBannerVisibility() {
  const userProfile = useCurrentUserProfile()
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const hasVisitedBefore = markVisitedAndGetHasVisitedBefore(userProfile.data?.id)
    const isDismissed = getIsDismissed(userProfile.data?.id)
    setIsVisible(hasVisitedBefore && !isDismissed)
  }, [userProfile.data?.id])

  const dismiss = useCallback(() => {
    if (!userProfile.data?.id) return
    const storageKey = `${STORAGE_KEY_PREFIX}_${DISMISSED_KEY_SUFFIX}_${userProfile.data?.id}`
    localStorage.setItem(storageKey, '1')
    setIsVisible(false)
  }, [userProfile.data?.id])

  return { isVisible, dismiss }
}
